import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClientService } from '../../services/client.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../services/subscription.service';
import { COUNTRY_TO_CURRENCY_MAP } from '../../../shared/constants/country-currency.constant';
import { CompanyClient } from '../../../shared';
import { User } from '../../models';

@Component({
  selector: 'kt-app-clients',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss'
})
export class ClientsComponent implements OnInit {
  private clientService = inject(ClientService);
  private userService = inject(UserService);

  clients = signal<CompanyClient[]>([]);
  currentUser: User | null = null;
  clientForm!: FormGroup;
  fb = inject(FormBuilder);
  showAddClientForm = false;
  editingClientId: string | null = null;
  private subscriptionService = inject(SubscriptionService);

  defaultCountry = 'Canada';

  // Country to Currency mapping
    countryToCurrencyMap = COUNTRY_TO_CURRENCY_MAP;

  /** Whether the user can add another client (subscription + limit check). */
  get canAddClient(): boolean {
    return this.subscriptionService.canAddClient(this.clients().length);
  }

  ngOnInit(): void {
    this.currentUser = this.userService.currentUser();

    this.fetchClients();
    this.initializeForm();
  }

  private isValidUserId(userId: string): boolean {
    return Boolean(userId?.trim());
  }

  private fetchClients() {
    if (!this.currentUser || !this.isValidUserId(this.currentUser.id)) {
      this.clients.set([]);
      return;
    }

    this.clientService.getClients(this.currentUser.id).subscribe({
      next: (clients) => {
        this.clients.set(clients);
      },
      error: (error) => {
        console.error('Error fetching clients:', error);
        this.clients.set([]);
      }
    });
  }

  initializeForm(): void {
    this.clientForm = this.fb.group({
      clientName: ['', Validators.required],
      clientEmail: ['', [Validators.required, Validators.email]],
      clientPhone: [''],
      clientAddress: ['', Validators.required],
      clientCity: ['', Validators.required],
      clientProvince: ['', Validators.required],
      clientPostalCode: ['', Validators.required],
      clientCountry: ['Canada', Validators.required],
      clientWebsite: ['']
    });
  }

  toggleAddClientForm(): void {
    this.showAddClientForm = !this.showAddClientForm;
    if (!this.showAddClientForm) {
      this.editingClientId = null;
      this.clientForm.reset({ clientCountry: this.defaultCountry });
    }
  }

  onPremiumAddClientClick(): void {
    this.subscriptionService.checkAndShowPremiumForNewClient(this.clients().length);
  }

  editClient(client: CompanyClient): void {
    if(client.id) {
      this.editingClientId = client.id;
    } else {
      console.warn('Attempting to edit a client without a valid ID.');
      return;
    }
    
    this.clientForm.patchValue({
      clientName: client.name,
      clientEmail: client.email,
      clientPhone: client.phone ?? '',
      clientAddress: client.address?.address ?? '',
      clientCity: client.address?.city ?? '',
      clientProvince: client.address?.province ?? '',
      clientPostalCode: client.address?.postalCode ?? '',
      clientCountry: client.address?.country ?? this.defaultCountry,
      clientWebsite: client.websiteLink ?? ''
    });
    this.showAddClientForm = true;
  }

  onSubmit(): void {
    if (this.clientForm.invalid) {
      this.clientForm.markAllAsTouched();
      return;
    }
    if (this.editingClientId) {
      this.updateClient();
    } else {
      this.addClient();
    }
  }

  private addClient() {
    const clientDetails: CompanyClient = {
      id: '',
      userId: this.currentUser!.id,
      name: this.clientForm.value.clientName,
      phone: this.clientForm.value.clientPhone,
      email: this.clientForm.value.clientEmail,
      address: {
        address: this.clientForm.value.clientAddress,
        city: this.clientForm.value.clientCity,
        province: this.clientForm.value.clientProvince,
        postalCode: this.clientForm.value.clientPostalCode,
        country: this.clientForm.value.clientCountry,
        countryCurrency: this.countryToCurrencyMap[this.clientForm.value.clientCountry] || 'CAD'
      },
      websiteLink: this.clientForm.value.clientWebsite ?? ''
    };
    
    this.clientService.addClientDetails(clientDetails).subscribe({
      next: () => {
        this.clientForm.reset({ clientCountry: this.defaultCountry });
        this.showAddClientForm = false;
        this.fetchClients();
      },
      error: (error) => {
        console.error('Error adding client:', error);
      }
    });
  }

  private updateClient(): void {
    if (!this.editingClientId || !this.currentUser) return;
    const clientDetails: CompanyClient = {
      id: this.editingClientId,
      userId: this.currentUser.id,
      name: this.clientForm.value.clientName,
      phone: this.clientForm.value.clientPhone,
      email: this.clientForm.value.clientEmail,
      address: {
        address: this.clientForm.value.clientAddress,
        city: this.clientForm.value.clientCity,
        province: this.clientForm.value.clientProvince,
        postalCode: this.clientForm.value.clientPostalCode,
        country: this.clientForm.value.clientCountry,
        countryCurrency: this.countryToCurrencyMap[this.clientForm.value.clientCountry] || 'CAD'
      },
      websiteLink: this.clientForm.value.clientWebsite ?? ''
    };
    this.clientService.updateClientDetails(this.editingClientId, clientDetails).subscribe({
      next: () => {
        this.clientForm.reset({ clientCountry: this.defaultCountry });
        this.editingClientId = null;
        this.showAddClientForm = false;
        this.fetchClients();
      },
      error: (error) => {
        console.error('Error updating client:', error);
      }
    });
  }
}
